import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBox from "../../AdminComponents/Common/SearchBox";
import PaginationDataTable from "../../AdminComponents/Common/PaginationDataTable";
import Heading from "../../AdminComponents/Common/Heading";
import { BASE_URL } from "../../Assets/Constants";
import axios from "axios";
import { toast } from "react-toastify";
import { theme } from "../../Assets/Theme/theme";

function Users() {
  const navigate = useNavigate();
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const columns = [
    { id: "S.no", name: "S.no" },
    { id: "user", name: "User Name" },
    { id: "email", name: "Email" },
    { id: "action", name: "Action" },
  ];

  const fetchUsers = async () => {
    setLoading(true);
    setError("");

    try {
      const loginData = JSON.parse(localStorage.getItem("adminData"));
      const token = loginData?.token;

      if (!token) {
        toast.error("Authentication token is missing", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
        navigate('/login')
        return;
      }

      const response = await axios.get(`${BASE_URL}/admin/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUsersData(response?.data?.data);
      setFilteredData(response?.data?.data);
    } catch (err) {
      if (err.response?.status === 401 || err.message.includes("Authentication")) {
        navigate("/login");
      } else {
        setError(
          err.response?.data?.message || err.message || "Failed to fetch users."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [navigate]);

  const handleEdit = async (user) => {
    const loginData = JSON.parse(localStorage.getItem("adminData"));
    const token = loginData?.token;

    if (!token) {
      toast.error("Authentication token is missing", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      navigate('/login')
      return;
    }

    try {
      const response = await axios.get(`${BASE_URL}/admin/user/${user._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      navigate("/admin/edit-user", { state: response.data });
    } catch (err) {
      if (err.response?.status === 401 || err.message.includes("Authentication")) {
        navigate("/login");
      } else {
        console.error(
          "Failed to fetch user data:",
          err.response?.data || err.message
        );
      }
    }
  };

  const handleOpenModal = (userId) => {
    setSelectedUserId(userId);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedUserId(null);
  };

  const handleDelete = async () => {
    const loginData = JSON.parse(localStorage.getItem("adminData"));
    const token = loginData?.token;

    if (!token) {
      toast.error("Authentication token is missing", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      navigate('/login')
      return;
    }

    try {
      const response = await axios.delete(
        `${BASE_URL}/admin/user/${selectedUserId}/delete`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUsersData((prevData) =>
        prevData.filter((user) => user._id !== selectedUserId)
      );

      handleCloseModal();
      fetchUsers();
      toast.success(response?.data?.message, {
        position: "top-right",
        autoClose: 3000,
        theme: "colored",
        style: { backgroundColor: 'orange', color: 'white' },
      });
    } catch (err) {
      console.error(
        "Failed to delete user:",
        err.response?.data || err.message
      );

    }
  };

  const [currentPage, setCurrentPage] = useState(0); 

  const handleSearchChange = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchQuery(value);
    setCurrentPage(0); 
    const filtered = usersData.filter((item) =>
      item.user?.toLowerCase().includes(value)
    );

    setFilteredData(filtered);
  };

  return (
    <Box>
      <Heading label={"Users"} />
      <Box mt={4}>
        <SearchBox
          placeholder={"Search by name"}
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </Box>
      <Box>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <CircularProgress size={30} sx={{ color: theme.colors.orange }} />
          </Box>
        ) : error ? (
          <p style={{ color: "red" }}>{error}</p>
        ) : (
          <PaginationDataTable
            editAndDelete={true}
            columns={columns}
            rows={filteredData}
            onDelete={(user) => handleOpenModal(user._id)}
            onEdit={(user) => handleEdit(user)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </Box>

      {/* Delete Confirmation Modal */}
      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="black" sx={{
              backgroundColor: theme.colors.orange,
              borderRadius: '5px',
              color: 'white',
              '&:hover': {
                backgroundColor: 'darkorange',
              },
            }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="primary" sx={{
              backgroundColor: theme.colors.red,
              borderRadius: '5px',
              color: 'white',
              '&:hover': {
                backgroundColor: '#FF474C',
              },
            }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;
